import React, { useCallback, useMemo, useState } from 'react';
import { TouchTarget } from 'apps/sigma2-ui/src/components/ui/TouchTarget';
import { TouchTargetBehavior } from 'apps/sigma2-ui/src/components/ui/TouchTarget';
import cn from 'classnames';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { BookmarkIconSvg } from 'apps/sigma2-ui/src/components/svg/BookmarkIcon';
import { BookmarkOutlineIconSvg } from 'apps/sigma2-ui/src/components/svg/BookmarkOutlineIcon';
import { PrimaryFilterSelection } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { SecondaryFilterSelection } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { FocusArea } from 'libs/end-to-end/src/focus-area';
import { CircularProgress } from '@mui/material';
import { useSavedAnalysis } from 'apps/sigma2-ui/src/filters-v2/lib/useSavedAnalysis';
import { useAsyncEffect } from 'apps/sigma2-ui/src/lib/hooks/useAsyncEffect';
import { CustomVizRequest } from 'libs/end-to-end/src/custom-viz/CustomVizRequest';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { ViewMode } from 'libs/end-to-end/src/view-configurations';

export interface GetCustomVizRequestArgs {
  primaryFilterSelections: PrimaryFilterSelection[];
  secondaryFilterSelections: SecondaryFilterSelection[];
  focusArea: FocusArea | null;
}

export interface SaveAnalysisButtonProps {
  /**
   * Whether the button is disabled
   */
  disabled: boolean;
  getCustomVizRequest: () => {
    customVizRequest: CustomVizRequest | undefined;
    defaultViewMode?: ViewMode;
    selectedMetric?: string | null;
  };
}

export const SaveAnalysisButton = ({
  disabled,
  getCustomVizRequest,
}: SaveAnalysisButtonProps) => {
  const language = useLanguage();
  const [isLoading, setIsLoading] = useState(false);

  const [savedAnalysis, setSavedAnalysis] = useState<string | null>(null);
  const isAnalysisSaved = savedAnalysis !== null;

  const { createAnalysis, getSavedAnalysisByCustomVizRequest, deleteAnalysis } =
    useSavedAnalysis();

  useAsyncEffect(async () => {
    const { customVizRequest } = getCustomVizRequest();

    if (!customVizRequest) {
      return;
    }

    const response = await getSavedAnalysisByCustomVizRequest({
      customVizRequest,
    });

    if (response?.resultType === 'ok') {
      setSavedAnalysis(response.value.savedAnalysisId);
    } else {
      setSavedAnalysis(null);
    }
  }, [getSavedAnalysisByCustomVizRequest, getCustomVizRequest]);

  const handleOnSave = useCallback(async () => {
    setIsLoading(true);

    const { customVizRequest } = getCustomVizRequest();

    if (!customVizRequest) {
      return;
    }

    const response = await createAnalysis({
      customVizRequest,
    });

    if (response?.resultType === 'ok') {
      setSavedAnalysis(response.value.savedAnalysisId);
    }

    setIsLoading(false);
  }, [createAnalysis, getCustomVizRequest]);

  const handleOnUnSave = useCallback(async () => {
    setIsLoading(true);
    const response = await deleteAnalysis({ savedAnalysisId: savedAnalysis! });

    if (response?.resultType === 'ok') {
      setSavedAnalysis(null);
    }
    setIsLoading(false);
  }, [deleteAnalysis, savedAnalysis]);

  const handleClick = useCallback(() => {
    if (isAnalysisSaved) {
      handleOnUnSave();
    } else {
      handleOnSave();
    }
  }, [handleOnSave, handleOnUnSave, isAnalysisSaved]);

  const behavior = useMemo<TouchTargetBehavior>(
    () => ({ type: 'onClick', onClick: handleClick }),
    [handleClick],
  );

  const Icon = isAnalysisSaved ? BookmarkIconSvg : BookmarkOutlineIconSvg;

  return (
    <div className={'inline-block max-w-full'}>
      <TouchTarget
        disabled={disabled}
        content={({ hovered }) => {
          return (
            <span
              className={cn(
                'flex rounded py-2 px-3 border-[1px] gap-2 items-center transition-all',
                {
                  'opacity-50 cursor-not-allowed': disabled,
                  'opacity-100 cursor-pointer': !disabled,
                  'bg-white border-gray-200': !hovered,
                  'bg-royal-100 border-royal-400': hovered || isAnalysisSaved,
                },
              )}
            >
              {isLoading ? (
                <CircularProgress
                  size={16}
                  className={cn({
                    'text-royal-700': hovered || isAnalysisSaved,
                    'text-gray-900': !hovered,
                  })}
                />
              ) : (
                <Icon
                  svg={{
                    className: cn('transition-all', {
                      'text-royal-700': hovered || isAnalysisSaved,
                      'text-gray-900': !hovered,
                      'w-[1em] h-[1em]': isAnalysisSaved,
                    }),
                    width: '18px',
                    height: '18px',
                  }}
                />
              )}

              <S2Typography
                translatedContent={
                  isAnalysisSaved ? language.unsave() : language.save()
                }
                variant={'headline-7-alt'}
                textColorVariant={
                  hovered || isAnalysisSaved ? 'royal-700' : 'gray-900'
                }
              />
            </span>
          );
        }}
        behavior={behavior}
      ></TouchTarget>
    </div>
  );
};
